import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useState, useRef } from 'react';
import SharedAutocomplete from '../../../../../../shared/shared-autocomplete.tsx';
import UserAvatar from '../../../../../../shared/user-avatar.tsx';
import { DecomPolicyFormValues } from '../../decom-policy-form-layout.tsx';
import { DestUser } from '../../../../../../../types.ts';

import { clearFieldError } from '../decom-action.tsx';
import { ActionErrorEntry } from '../../decom-policy-utils.tsx';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Clear from '@mui/icons-material/Clear';

interface DecomReportRecipientInputProps {
  day: number;
  idx: number;
  control: Control<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  actionType: string;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  destUserFields: (DestUser & { id: string })[];
  destUserAppend: (value: DestUser) => void;
  destUserRemove: (index?: number) => void;
  destUserUpdate: (index: number, value: DestUser) => void;
  titleLabel?: string;
  customLabel?: string;
  autocompleteLabel?: string;
}

export default function DecomReportRecipientInput({
  day,
  idx,
  control,
  watch,
  setValue,
  clearErrors,
  errors,
  actionType,
  setActionTypesWithErrors,
  destUserFields,
  destUserAppend,
  destUserRemove,
  destUserUpdate,
  titleLabel = 'Report Recipient Email:',
  customLabel = 'Custom User or Group:',
  autocompleteLabel,
}: DecomReportRecipientInputProps) {
  const useManagerEmail = watch(
    `actions.${day}.actions.${idx}.useManagerEmail`
  );

  const radioSelection =
    useManagerEmail === true
      ? 'manager'
      : useManagerEmail === false
      ? 'custom'
      : '';

  const [autoCompleteResponseDestUser, setAutoCompleteResponseDestUser] =
    useState('');
  const selectedDestUserTypeRef = useRef<'user' | 'group'>('user');

  const handleSetDestUser = (value: string) => {
    setAutoCompleteResponseDestUser(value);
    clearErrors(`actions.${day}.actions.${idx}.destUser`);
    if (destUserFields.find((userDest) => userDest.userEmail === value)) return;
    if (value === '') return;
    const hasOwner = destUserFields.some(
      (userDest) => userDest.role === 'owner'
    );
    let roleToAssign: 'owner' | 'reader' = 'owner';
    if (selectedDestUserTypeRef.current === 'group' || hasOwner) {
      roleToAssign = 'reader';
    }
    destUserAppend({
      userEmail: value,
      role: roleToAssign,
      type: selectedDestUserTypeRef.current,
    });
    setAutoCompleteResponseDestUser('');
    clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
  };

  const handleRadioChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (...event: any[]) => void
  ) => {
    const selected = e.target.value;
    if (selected === 'manager') {
      fieldOnChange(true);
      setValue(`actions.${day}.actions.${idx}.useManagerEmail`, true, {
        shouldValidate: true,
        shouldDirty: true,
      });
      destUserRemove();
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
      clearFieldError(actionType, 'useManagerEmail', setActionTypesWithErrors);
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
    } else if (selected === 'custom') {
      fieldOnChange(false);
      setValue(`actions.${day}.actions.${idx}.useManagerEmail`, false, {
        shouldValidate: true,
        shouldDirty: true,
      });
      destUserRemove();
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
      clearFieldError(actionType, 'useManagerEmail', setActionTypesWithErrors);
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
    } else {
      fieldOnChange(undefined);
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
    }
  };

  const handleRoleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const newRole = e.target.value as
      | 'owner'
      | 'reader'
      | 'commenter'
      | 'writer';
    destUserUpdate(index, { ...destUserFields[index], role: newRole });
    if (newRole === 'owner') {
      clearErrors(`actions.${day}.actions.${idx}.destUser`);
      clearFieldError(actionType, 'destUser', setActionTypesWithErrors);
    }
  };

  return (
    <Controller
      name={`actions.${day}.actions.${idx}.useManagerEmail`}
      control={control}
      defaultValue={undefined}
      render={({ field }) => (
        <FormControl
          component="fieldset"
          error={!!errors?.actions?.[day]?.actions?.[idx]?.useManagerEmail}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {titleLabel}
          </Typography>
          {!!errors?.actions?.[day]?.actions?.[idx]?.useManagerEmail && (
            <FormHelperText>
              {errors?.actions?.[day]?.actions?.[idx]?.useManagerEmail?.message}
            </FormHelperText>
          )}
          <RadioGroup
            value={radioSelection}
            onChange={(e) => handleRadioChange(e, field.onChange)}
          >
            <FormControlLabel
              value="manager"
              control={<Radio />}
              sx={{ width: 'fit-content' }}
              label="Manager"
            />
            <Stack direction="row" alignItems="flex-start">
              <FormControlLabel
                value="custom"
                control={<Radio />}
                sx={{ width: 'fit-content', mt: 0 }}
                label={customLabel}
              />
              <Controller
                name={`actions.${day}.actions.${idx}.destUser`}
                control={control}
                render={() => (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ width: 360 }}>
                      <SharedAutocomplete
                        sources={['users', 'groups']}
                        autocompleteValue={autoCompleteResponseDestUser}
                        setAutoCompleteTypeValue={(value: string) => {
                          if (value === 'user' || value === 'group') {
                            selectedDestUserTypeRef.current = value;
                          }
                        }}
                        setAutoCompleteValue={handleSetDestUser}
                        errors={
                          !!errors.actions?.[day]?.actions?.[idx]?.destUser
                        }
                        disabled={radioSelection !== 'custom'}
                        label={autocompleteLabel}
                        helperText={''}
                      />
                    </Box>
                    <FormHelperText
                      error={!!errors.actions?.[day]?.actions?.[idx]?.destUser}
                      sx={{ mx: 1 }}
                    >
                      {errors.actions?.[day]?.actions?.[idx]?.destUser
                        ?.message || ' '}
                    </FormHelperText>
                  </Box>
                )}
              />
            </Stack>
            <List sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
              {destUserFields &&
                destUserFields.map((userDest, index) => {
                  const hasOwner = destUserFields.some(
                    (u) => u.role === 'owner'
                  );
                  return (
                    <Stack key={userDest.id}>
                      <ListItem
                        dense
                        sx={{ maxWidth: 650 }}
                        secondaryAction={
                          <Stack
                            flexDirection="row"
                            gap={1}
                            alignItems="center"
                          >
                            <TextField
                              value={userDest.role}
                              onChange={(e) => handleRoleChange(e, index)}
                              select
                              label="Role"
                              size="small"
                              sx={{ width: 175 }}
                            >
                              {userDest.type !== 'group' && (
                                <MenuItem
                                  value="owner"
                                  disabled={
                                    hasOwner && userDest.role !== 'owner'
                                  }
                                >
                                  Owner
                                </MenuItem>
                              )}
                              <MenuItem value="reader">Viewer</MenuItem>
                              <MenuItem value="commenter">Commenter</MenuItem>
                              <MenuItem value="writer">Editor</MenuItem>
                            </TextField>
                            <Tooltip arrow title="Remove">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  destUserRemove(index);
                                  clearErrors(
                                    `actions.${day}.actions.${idx}.destUser`
                                  );
                                }}
                              >
                                <Clear />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        }
                      >
                        <ListItemAvatar>
                          <UserAvatar userEmail={userDest.userEmail || ''} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={userDest.userEmail}
                          primaryTypographyProps={{
                            maxWidth: 300,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                        />
                      </ListItem>
                    </Stack>
                  );
                })}
            </List>
          </RadioGroup>
        </FormControl>
      )}
    />
  );
}
